import { useQuery } from '@tanstack/react-query';
import { ACTION_LABELS, type ActionType, type AutomationAction } from '#shared';
import { api } from '../../lib/api';
import { useLeadSetup } from '../../lib/leads';
import { useWaTemplates } from '../../lib/whatsapp';
import { SelectField, TextField } from '../ui';

export interface SequenceSummary { id: string; name: string; isActive: boolean }
export const useSequences = () => useQuery({ queryKey: ['sequences'], queryFn: () => api.get<(SequenceSummary & Record<string, unknown>)[]>('/sequences') });

/** A blank action of each type, ready to fill in. */
export function blankAction(type: ActionType): AutomationAction {
  switch (type) {
    case 'assign': return { type, userId: null };
    case 'add_tag': return { type, tagId: '' };
    case 'set_status': return { type, statusId: '' };
    case 'set_followup': return { type, inHours: 24, note: '' };
    case 'notify': return { type, to: 'owner', message: '' };
    case 'whatsapp_template': return { type, templateName: '', language: 'en', params: [] };
    case 'enroll_sequence': return { type, sequenceId: '' };
  }
}

const HOURS = [0, 1, 2, 4, 24, 48, 72, 168];
const hoursLabel = (h: number) => (h === 0 ? 'Right away' : h < 24 ? `In ${h} hour${h === 1 ? '' : 's'}` : `In ${h / 24} day${h === 24 ? '' : 's'}`);

/** Edits one action. `types` limits which kinds can be picked (sequences allow fewer). */
export function ActionEditor({ value, onChange, types, error }: {
  value: AutomationAction; onChange: (a: AutomationAction) => void; types: readonly ActionType[]; error?: string;
}) {
  const setup = useLeadSetup().data;
  const templates = useWaTemplates();
  const sequences = useSequences();
  const a = value;
  const approved = (templates.data ?? []).filter((t) => t.status === 'APPROVED');
  const tpl = a.type === 'whatsapp_template' ? approved.find((t) => t.name === a.templateName && t.language === a.language) : undefined;

  return (
    <div className="space-y-3">
      <SelectField label="Do this" value={a.type} onChange={(e) => onChange(blankAction(e.target.value as ActionType))}>
        {types.map((t) => <option key={t} value={t}>{ACTION_LABELS[t]}</option>)}
      </SelectField>
      {a.type === 'assign' && (
        <SelectField label="Give it to" value={a.userId ?? ''} onChange={(e) => onChange({ ...a, userId: e.target.value || null })}>
          <option value="">Use the auto-assign rules (round robin)</option>
          {setup?.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </SelectField>
      )}
      {a.type === 'add_tag' && (
        <SelectField label="Tag" value={a.tagId} error={error} onChange={(e) => onChange({ ...a, tagId: e.target.value })}>
          <option value="">— Choose —</option>
          {setup?.tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </SelectField>
      )}
      {a.type === 'set_status' && (
        <SelectField label="New status" value={a.statusId} error={error} onChange={(e) => onChange({ ...a, statusId: e.target.value })}>
          <option value="">— Choose —</option>
          {setup?.statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </SelectField>
      )}
      {a.type === 'set_followup' && (
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectField label="When" value={String(a.inHours)} onChange={(e) => onChange({ ...a, inHours: Number(e.target.value) })}>
            {HOURS.map((h) => <option key={h} value={h}>{hoursLabel(h)}</option>)}
          </SelectField>
          <TextField label="Note" value={a.note ?? ''} onChange={(e) => onChange({ ...a, note: e.target.value })} placeholder="e.g. Call {{name}} back" />
        </div>
      )}
      {a.type === 'notify' && (
        <>
          <SelectField label="Tell" value={a.to} onChange={(e) => onChange({ ...a, to: e.target.value })}>
            <option value="owner">The lead’s salesperson</option>
            <option value="managers">Admins and managers</option>
            {setup?.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </SelectField>
          <TextField label="Message" value={a.message} error={error} onChange={(e) => onChange({ ...a, message: e.target.value })} placeholder="e.g. Call {{name}} now — new Facebook lead" />
        </>
      )}
      {a.type === 'whatsapp_template' && (
        approved.length === 0 ? (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-950 dark:text-amber-200">No approved WhatsApp templates yet. Connect WhatsApp and sync templates in Settings → WhatsApp setup.</p>
        ) : (
          <>
            <SelectField label="Template" value={`${a.templateName}|${a.language}`} error={error}
              onChange={(e) => { const [templateName = '', language = 'en'] = e.target.value.split('|'); onChange({ ...a, templateName, language, params: [] }); }}>
              <option value="|en">— Choose —</option>
              {approved.map((t) => <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>{t.name} ({t.language})</option>)}
            </SelectField>
            {tpl?.bodyText && <p className="rounded-lg bg-slate-50 px-3 py-2 text-sm whitespace-pre-wrap dark:bg-slate-800">{tpl.bodyText}</p>}
            {Array.from({ length: tpl?.paramCount ?? 0 }, (_, i) => (
              <TextField key={i} label={`Value for {{${i + 1}}}`} value={a.params[i] ?? ''} placeholder={i === 0 ? '{{name}}' : ''}
                onChange={(e) => { const params = [...a.params]; params[i] = e.target.value; onChange({ ...a, params }); }} />
            ))}
          </>
        )
      )}
      {a.type === 'enroll_sequence' && (
        <SelectField label="Sequence" value={a.sequenceId} error={error} onChange={(e) => onChange({ ...a, sequenceId: e.target.value })}
          hint={sequences.data?.length === 0 ? 'Create a sequence first (Sequences tab).' : undefined}>
          <option value="">— Choose —</option>
          {sequences.data?.filter((s) => s.isActive).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </SelectField>
      )}
      {(a.type === 'notify' || a.type === 'set_followup' || a.type === 'whatsapp_template') && (
        <p className="text-xs text-slate-500">You can use {'{{name}}'}, {'{{company}}'}, {'{{city}}'}, {'{{owner}}'} — they’re replaced with the lead’s details.</p>
      )}
    </div>
  );
}
